/**
 * Fila local de respostas do checklist.
 *
 * O servidor já salva cada resposta (upsert por item). O que faltava era o que
 * acontece quando a rede cai no pátio: a resposta dada sumia. Aqui toda resposta
 * passa PRIMEIRO pela fila no `localStorage`, e sai dela só quando a API confirma.
 *
 * - Chave por execução: `bi:checklist-outbox:<runId>`.
 * - Um registro por item: responder de novo o mesmo item substitui o pendente
 *   (vale a última resposta, como no servidor).
 * - Reenviar é seguro: o PUT é idempotente.
 *
 * O storage é injetável para teste; no browser é `window.localStorage`. Se o
 * storage falhar (modo privado, cota), a fila vira memória — a tela continua.
 */
export interface PendingAnswer {
  itemId: string;
  optionValue?: string | null;
  numberValue?: number | null;
  textValue?: string | null;
  /** mapa de avarias (`{ marks }`) */
  jsonValue?: { marks: Array<{ id: string; view: string; x: number; y: number; note: string | null }> } | null;
  observation?: string | null;
  criticality?: "low" | "medium" | "high" | null;
  /** problema pré-cadastrado escolhido */
  defectCode?: string | null;
  /** item de pneu: qual pneu/posição (gestão de pneus) */
  tireId?: string | null;
  tirePositionId?: string | null;
  /** epoch ms — para mostrar "aguardando desde" e ordenar o envio */
  queuedAt: number;
}

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const PREFIXO = "bi:checklist-outbox:";

function memoria(): KeyValueStorage {
  const m = new Map<string, string>();
  return { getItem: (k) => m.get(k) ?? null, setItem: (k, v) => void m.set(k, v), removeItem: (k) => void m.delete(k) };
}

function storagePadrao(): KeyValueStorage {
  try {
    const ls = globalThis.localStorage;
    if (ls) {
      const teste = `${PREFIXO}__probe`;
      ls.setItem(teste, "1");
      ls.removeItem(teste);
      return ls;
    }
  } catch {
    // cai para memória
  }
  return memoria();
}

export class AnswerOutbox {
  private readonly key: string;
  constructor(runId: string, private readonly storage: KeyValueStorage = storagePadrao()) {
    this.key = `${PREFIXO}${runId}`;
  }

  list(): PendingAnswer[] {
    try {
      const raw = this.storage.getItem(this.key);
      const lista = raw ? (JSON.parse(raw) as PendingAnswer[]) : [];
      return Array.isArray(lista) ? lista.sort((a, b) => a.queuedAt - b.queuedAt) : [];
    } catch {
      return [];
    }
  }

  private write(lista: PendingAnswer[]) {
    try {
      if (lista.length === 0) this.storage.removeItem(this.key);
      else this.storage.setItem(this.key, JSON.stringify(lista));
    } catch {
      // storage cheio: o envio imediato ainda acontece; só a retomada offline se perde
    }
  }

  /** Enfileira (substitui o pendente do mesmo item, mesclando campos). */
  enqueue(answer: Omit<PendingAnswer, "queuedAt">, now: number = Date.now()): PendingAnswer {
    const lista = this.list();
    const anterior = lista.find((a) => a.itemId === answer.itemId);
    const novo: PendingAnswer = { ...anterior, ...answer, queuedAt: now };
    this.write([...lista.filter((a) => a.itemId !== answer.itemId), novo]);
    return novo;
  }

  /**
   * Confirma o envio. Só remove se o pendente for o MESMO que foi enviado: se o
   * usuário respondeu de novo enquanto a requisição viajava, o mais novo fica.
   */
  acknowledge(sent: PendingAnswer[]) {
    const enviados = new Map(sent.map((s) => [s.itemId, s.queuedAt]));
    this.write(this.list().filter((a) => enviados.get(a.itemId) !== a.queuedAt));
  }

  clear() {
    this.write([]);
  }

  /** Valor que a tela deve mostrar: pendente local por cima do servidor. */
  overlay<T extends { itemId: string }>(serverAnswers: T[]): Map<string, T | (Partial<T> & PendingAnswer)> {
    const m = new Map<string, T | (Partial<T> & PendingAnswer)>(serverAnswers.map((a) => [a.itemId, a]));
    for (const p of this.list()) m.set(p.itemId, { ...(m.get(p.itemId) ?? {}), ...p } as Partial<T> & PendingAnswer);
    return m;
  }
}

/** Remove do payload os campos de controle local antes de mandar para a API. */
export function toApiPayload(p: PendingAnswer) {
  const { queuedAt: _q, ...resto } = p;
  return resto;
}
